import React, {useCallback, useEffect, useMemo, useState} from 'react'
import './FilterCreationScreen.scss'
import { AppLayout } from 'app_components/layouts/app'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import { IoIosAdd } from 'react-icons/io'
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'
import { MdDelete, MdEdit } from 'react-icons/md'
import ModalForm from '../FilterCoursesForm/FilterCoursesForm'
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import { useTranslation } from 'react-i18next'
import TermService from 'app_services/admin/term/termService'
import { useParams } from 'react-router-dom'
import { ITerm, Terms } from 'app_types/admin/term/term.types'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import SwitchComponent from 'app_components/commons/switch'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'


const FilterCreationScreen = () => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false);
  const [termCategory, setTermCategory] = useState<ITerm<{ typeRelated: Terms, publish: boolean }>>()
  const [terms, setTerms] = useState<ITerm[]>([])
  const [termToEdit, setTermToEdit] = useState<ITerm>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const termService = useMemo(() => new TermService(), [])
  const notificationsUtil = useMemo(() => new ScreenNotificationsUtil(), [])
  const [shouldPublish, setShouldPublish] = useState<boolean>(false)
  const { termId } = useParams<{ termId: string }>()

  const getTermCategory = useCallback(async () => {
    if (!termId) return
    const response = await termService.getById({ id: termId })
    if (response?.code === 200 && response?.term) {
      setTermCategory(response.term)
    }
  }, [termService, termId])

  const handleClose = () => {
    setShow(false)
    setTermToEdit(undefined)
  };
  const handleShow = () => setShow(true);

  const fetchTerms = useCallback(async () => {
    if (!termCategory?.custom?.typeRelated) return
    setIsLoading(true)
    const response = await termService.list({
      nPerPage: '100',
      enabled: true,
      type: termCategory.custom.typeRelated,
    })
    if (response?.code === 200 && response?.terms) {
      setTerms(response.terms)
    }
    setIsLoading(false)
  }, [termService, termCategory])

  const handleCreateOrUpdate = useCallback(async (name: string, id?: string) => {
    if (!termCategory?.custom?.typeRelated) return
    let response
    if (id) {
      response = await termService.update({
        type: termCategory.custom.typeRelated,
        name,
        id,
      })
    } else {
      response = await termService.create({
        type: termCategory.custom.typeRelated,
        name,
      })
    }
    if (response?.code === 200) {
      notificationsUtil.showSuccessTimer()
      fetchTerms()
    }
  }, [termService, termCategory, notificationsUtil, fetchTerms])

  const onEdit = (term: ITerm) => {
    setTermToEdit(term)
    setShow(true)
  }

  const onDelete = useCallback(async (id: string) => {
    if (!id) return
    const result = await notificationsUtil.showAskDelete()
    if (result.isConfirmed) {
      const response = await termService.delete({ id })
      if (response?.code === 200) {
        notificationsUtil.showSuccessTimer()
        fetchTerms()
      }
    }
  }, [notificationsUtil, termService, fetchTerms])

  const handleClickPublish = useCallback(async () => {
    if (!termCategory?._id) return
    const newPublish = !shouldPublish
    setShouldPublish(newPublish)
    const response = await termService.update({
      id: termCategory._id,
      custom: {
        ...termCategory.custom,
        publish: newPublish
      }
    })
    if (response?.code === 200) {
      getTermCategory()
    }
  }, [getTermCategory, shouldPublish, termCategory, termService])

  useEffect(() => {
    getTermCategory()
  }, [getTermCategory])

  useEffect(() => {
    fetchTerms()
  }, [fetchTerms])

  useEffect(() => {
    if (termCategory?._id) {
      setShouldPublish(Boolean(termCategory.custom?.publish))
    }
  }, [termCategory?._id])

  return (
    <AppLayout>
      <div className='filter-creation-screen--container'>
        <ModalForm show={show} handleClose={handleClose} onCreate={handleCreateOrUpdate} title={`${termToEdit ? 'Editar' : 'Nuevo'} ${termCategory?.name}`} termToEdit={termToEdit}/>
        <BreadcrumbComponent links={['/admin/courses', '#']} texts={[`${t('components.breadcrumb.courses')}`, 'Filtros publicos']} />
        <div className='filter-creation-screen--title'>{termCategory?.name}</div>
        {termCategory?._id && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%', maxWidth: '500px', justifyContent: 'space-between' }}>
            <div className='filter-creation-screen--button-add'>
              <ButtonComponent
                variant="primary"
                onClick={() => handleShow()}
                loading={isLoading}
              >
                <ButtonComponent.Icon>
                  <IoIosAdd />
                </ButtonComponent.Icon>
                Nuevo {termCategory?.name}
              </ButtonComponent>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center'}}>
              <LabelComponent>Publicar categoría</LabelComponent>
              <SwitchComponent checked={shouldPublish} onClick={handleClickPublish} />
            </div>
          </div>
        )}
        <LoadingWrapper loading={isLoading}>
          <>
            {terms?.map((term) => (
              <div key={term._id} className='filter-creation-screen--container-item'>
                <div className='filter-creation-screen--card'>{term.name}</div>
                <div className='filter-creation-screen--container-icons'>
                      <TooltipComponent text="Editar">
                    <div className='tutorial-admin-card--icon' onClick={() => onEdit(term)} style={{color: 'var(--primary)'}}>
                      <MdEdit size="20px" style={{cursor: 'pointer'}} />
                    </div>
                    </TooltipComponent>
                    <TooltipComponent text='Eliminar'>
                    <div className='tutorial-admin-card--icon' onClick={() => onDelete(term._id)} style={{color: 'var(--primary)'}}>
                      <MdDelete size="20px" style={{cursor: 'pointer'}}/>
                    </div>
                    </TooltipComponent>
                </div>
              </div>
            ))}
            {!terms?.length && (
              <TextComponent type='text'>
                Aún no se han creado items
              </TextComponent>
            )}
          </>
        </LoadingWrapper>
      </div>
    </AppLayout>

  )
}

export default FilterCreationScreen
